import { List, Map } from 'immutable';
import getIn from 'lodash/get';

import * as CatalogConstants from '../../catalog/constants';

import * as APSConstants from './constants';
import * as D from './deps';
import * as APSUtils from './utils';

export const INITIAL_STATE = Map({
  [APSConstants.APSKeys.CURRENT_ITEM]: Map(),
  [APSConstants.APSKeys.LYRIC_DATA]: Map(),
  [APSConstants.APSKeys.NARRATIVE]: Map(),
  [APSConstants.APSKeys.NEXT_ITEM]: Map(),
  [APSConstants.APSKeys.SESSION_ITEMS]: Map(),
  [APSConstants.APSKeys.SESSION_LIST]: List(),
  [APSConstants.APSKeys.SOURCE]: Map(),
});

export default D.createReducer(INITIAL_STATE, {
  [APSConstants.APSActions.SET_CURRENT_ITEM]: setCurrentItem,
  [APSConstants.APSActions.RESET_APS]: setInitialSate,
  [D.Constants.Api.ApiActions.ACTION_THUMB_UP_SUCCESS]: onThumbUpSuccess,
  [D.Constants.Api.ApiActions.ACTION_THUMB_DOWN_SUCCESS]: onThumbDownSuccess,
  [D.Constants.Api.ApiActions.ACTION_REMOVE_THUMB_SUCCESS]: onRemoveThumbSuccess,
  [D.Constants.Api.ApiActions.ACTION_REPEAT_ATTEMPT]: updateSourceRepeat,
  [D.Constants.Api.ApiActions.ACTION_REPEAT_FAIL]: revertSourceRepeat,
  [D.Constants.Api.ApiActions.ACTION_SHUFFLE_ATTEMPT]: updateSourceShuffle,
  [D.Constants.Api.ApiActions.ACTION_SHUFFLE_FAIL]: revertSourceShuffle,
  [D.Constants.Api.ApiActions.AESOP_REMOVE_EPISODES_FEEDBACK_SUCCESS]:
    onRemovePodcastEpisodeSuccess,
  [D.Constants.Api.ApiActions.PLAYBACK_PEEK_FAIL]: setNextItem, // still want to set expiry
  [D.Constants.Api.ApiActions.PLAYBACK_PEEK_SUCCESS]: setNextItem,
  [D.Constants.Api.ApiActions.PLAYBACK_SOURCE_SUCCESS]: setSource,
  [D.Constants.Api.ApiActions.PLAYBACK_CURRENT_SUCCESS]: setLyrics,
  [D.Constants.Api.ApiActions.ACTION_MODE_SUCCESS]: setLyrics,
  [D.Constants.Api.ApiActions.ACTION_SKIP_SUCCESS]: setLyrics,
  [D.Constants.Api.ApiActions.FETCH_NARRATIVE_SUCCESS]: fetchNarrativeSuccess,
  [D.Constants.Integrations.ActionTypes.TRACK_STARTED_FROM_SOURCE]: trackStarted,
  [D.Constants.Api.ApiActions.FLAG_MESSAGE_SUCCESS]: flagMessageSuccess,
});

function setInitialSate() {
  return INITIAL_STATE;
}

function handleRatingUpdate(state, action, rating) {
  const pandoraId = getIn(action, 'params.content.pandoraId');
  const currentRating =
    pandoraId === state.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'pandoraId'])
      ? rating
      : state.getIn([APSConstants.APSKeys.CURRENT_ITEM, APSConstants.APSKeys.RATING]);

  return state.withMutations(substate => {
    substate
      .setIn([APSConstants.APSKeys.CURRENT_ITEM, APSConstants.APSKeys.RATING], currentRating)
      .setIn([APSConstants.APSKeys.SESSION_ITEMS, pandoraId, APSConstants.APSKeys.RATING], rating)
      .set(APSConstants.APSKeys.NEXT_ITEM, INITIAL_STATE.get(APSConstants.APSKeys.NEXT_ITEM));
  });
}

function onThumbUpSuccess(state, action) {
  return handleRatingUpdate(state, action, 1);
}

function onThumbDownSuccess(state, action) {
  return handleRatingUpdate(state, action, -1);
}

function onRemoveThumbSuccess(state, action) {
  return handleRatingUpdate(state, action, 0);
}

function onRemovePodcastEpisodeSuccess(state, action) {
  return handleRatingUpdate(state, action, 0);
}

function setCurrentItem(state, action) {
  const prevItemSource = state.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'sourceId']);
  const item = getIn(action, 'data.item');
  const sessionItem = APSUtils.mapItemToSessionItem(item);
  let sessionItems = state
    .get(APSConstants.APSKeys.SESSION_ITEMS)
    .set(sessionItem.get('pandoraId'), sessionItem);
  let sessionList = state.get(APSConstants.APSKeys.SESSION_LIST);
  if (prevItemSource !== item.sourceId) {
    sessionItems = Map({ [sessionItem.get('pandoraId')]: sessionItem });
    sessionList = INITIAL_STATE.get(APSConstants.APSKeys.SESSION_LIST);
  }
  return state.withMutations(substate => {
    substate
      .set(APSConstants.APSKeys.CURRENT_ITEM, Map(item))
      .set(APSConstants.APSKeys.SESSION_ITEMS, sessionItems)
      .set(APSConstants.APSKeys.SESSION_LIST, sessionList);
  });
}

function setNextItem(state, action) {
  const item = getIn(action, 'response.item', {});
  item.timestamp = D.Utils.Time.getNow();
  return state.set(APSConstants.APSKeys.NEXT_ITEM, Map(item));
}

function trackStarted(state, { payload: { id, track } }) {
  const sessionLength =
    D.PlaybackConstants.SessionLengths[D.Utils.SourceType.getPandoraTypeFromPandoraId(id)] || 1;
  return state.update(APSConstants.APSKeys.SESSION_LIST, sessionList => {
    const newSessionList = sessionList.size === sessionLength ? sessionList.shift() : sessionList;
    return newSessionList.push(track.get('trackToken'));
  });
}

function setSource(state, action) {
  const itemId = getIn(action, 'response.item.itemId', null);
  const annotations = getIn(action, `response.annotations`, null);
  const trackAnnotation = getIn(annotations, itemId, null);
  const source = getIn(action, 'response.source', null);
  if (trackAnnotation?.explicitness === CatalogConstants.Explicitness.CLEAN) {
    return state.withMutations(substate => {
      substate
        .set(APSConstants.APSKeys.SOURCE, Map(source))
        .set(APSConstants.APSKeys.LYRIC_DATA, Map(getIn(trackAnnotation, 'cleanLyricData', {})));
    });
  }
  return state.withMutations(substate => {
    substate
      .set(APSConstants.APSKeys.SOURCE, Map(source))
      .set(APSConstants.APSKeys.LYRIC_DATA, Map(getIn(trackAnnotation, 'lyricData', {})));
  });
}

function setLyrics(state, action) {
  const itemId = getIn(action, 'response.item.itemId', null);
  const annotations = getIn(action, `response.annotations`, null);
  const trackAnnotation = getIn(annotations, itemId, null);
  if (trackAnnotation?.explicitness === CatalogConstants.Explicitness.CLEAN) {
    return state.withMutations(substate => {
      substate.set(
        APSConstants.APSKeys.LYRIC_DATA,
        Map(getIn(trackAnnotation, 'cleanLyricData', {})).set(
          APSConstants.APSKeys.NEXT_ITEM,
          INITIAL_STATE.get(APSConstants.APSKeys.NEXT_ITEM),
        ),
      );
    });
  }
  return state.withMutations(substate => {
    substate
      .set(APSConstants.APSKeys.LYRIC_DATA, Map(getIn(trackAnnotation, 'lyricData', {})))
      .set(APSConstants.APSKeys.NEXT_ITEM, INITIAL_STATE.get(APSConstants.APSKeys.NEXT_ITEM));
  });
}

function updateSourceRepeat(state, action) {
  if (
    state.getIn([APSConstants.APSKeys.SOURCE, 'pandoraId']) !==
    getIn(action, 'request.content.sourceId')
  ) {
    return state;
  }
  return state.withMutations(substate => {
    substate
      .setIn([APSConstants.APSKeys.SOURCE, 'repeat'], getIn(action, 'request.content.mode'))
      .set(APSConstants.APSKeys.NEXT_ITEM, INITIAL_STATE.get(APSConstants.APSKeys.NEXT_ITEM));
  });
}

function revertSourceRepeat(state, action) {
  if (
    state.getIn([APSConstants.APSKeys.SOURCE, 'pandoraId']) !==
    getIn(action, 'params.content.sourceId')
  ) {
    return state;
  }
  return state.setIn([APSConstants.APSKeys.SOURCE, 'repeat'], getIn(action, 'params.prevMode'));
}

function updateSourceShuffle(state, action) {
  if (
    state.getIn([APSConstants.APSKeys.SOURCE, 'pandoraId']) !==
    getIn(action, 'request.content.sourceId')
  ) {
    return state;
  }
  return state.withMutations(substate => {
    substate
      .setIn([APSConstants.APSKeys.SOURCE, 'shuffle'], getIn(action, 'request.content.enabled'))
      .set(APSConstants.APSKeys.NEXT_ITEM, INITIAL_STATE.get(APSConstants.APSKeys.NEXT_ITEM));
  });
}

function fetchNarrativeSuccess(state, action) {
  if (action.isAPSEnabled) {
    const data = {
      fromTrack: true,
      ...action.response,
    };
    return state
      .setIn([APSConstants.APSKeys.CURRENT_ITEM, 'narrative'], Map(data))
      .set(APSConstants.APSKeys.NARRATIVE, Map(data));
  }
  return state;
}

function revertSourceShuffle(state, action) {
  if (
    state.getIn([APSConstants.APSKeys.SOURCE, 'pandoraId']) !==
    getIn(action, 'params.content.sourceId')
  ) {
    return state;
  }
  return state.setIn(
    [APSConstants.APSKeys.SOURCE, 'shuffle'],
    !getIn(action, 'params.content.enabled'),
  );
}

function flagMessageSuccess(state, action) {
  const pandoraId = state.getIn([APSConstants.APSKeys.CURRENT_ITEM, 'pandoraId']);

  if (
    pandoraId &&
    pandoraId.split(':').pop() === getIn(action, 'request.content.artistMessageId')
  ) {
    return state.setIn([APSConstants.APSKeys.CURRENT_ITEM, 'isFlaggedMessage'], true);
  }

  return state;
}
